/**
 * GMA-1617 (CompassCare Web 2/14 — Overview) — Active Alerts rail.
 *
 * Renders active alerts sorted by severity then age (via the shared
 * overviewModel.sortAlertsBySeverityThenAge). Each row shows a severity/type
 * badge, the client, location, elapsed time (webFormat), and a quick
 * acknowledge action. Selecting a row raises onSelectAlert so the parent can
 * open the shared DetailDrawer — an alert reaches full detail in one click (the
 * "≤ 2 clicks" AC).
 *
 * Presentational + fail-safe: tolerates a null/empty list (renders an empty
 * state) and null/malformed rows. a11y: a labelled region; each row is a
 * listitem whose interactive open-region carries role="button" (keyboard
 * operable), and the Acknowledge <button> is a SIBLING of that region (not
 * nested), so activating Acknowledge never also opens the detail. Colors come
 * from the single design-token set.
 *
 * GMA-1608 (#330 AC-F1/F2/F4): new-alert arrivals are ANNOUNCED, not just
 * rendered — two always-mounted visually-hidden live regions (assertive for
 * critical/safety arrivals, polite for routine) speak the coalesced
 * announcement computed by the pure liveAnnouncement model. Dual regions avoid
 * a polite update clobbering (or being clobbered by) an assertive one.
 */
import React from "react";
import PropTypes from "prop-types";
import SeverityBadge from "./SeverityBadge";
import { sortAlertsBySeverityThenAge } from "../../pages/web/overviewModel";
import {
  buildAlertAnnouncement,
  VISUALLY_HIDDEN_STYLE,
} from "./liveAnnouncement";
import { formatRelativeTime } from "../../utils/webFormat";
import { theme } from "../../styles/designTokens";

export default function AlertsRail({ alerts, now, onSelectAlert, onAcknowledge }) {
  // The model tolerates (and returns) null/malformed rows; drop falsy rows here
  // so the render below can't dereference a null alert. Truthy-but-sparse rows
  // are still handled by per-field fallbacks.
  const sorted = sortAlertsBySeverityThenAge(alerts, now).filter(Boolean);
  const selectable = typeof onSelectAlert === "function";
  const ackable = typeof onAcknowledge === "function";

  // GMA-1608: announce arrivals between renders. The ref seeds with the mount
  // alerts, so pre-existing alerts are NOT announced on mount — only changes.
  // One effect run per committed alerts change = one coalesced announcement
  // per batch (AC-F4). The helper is fail-safe (null on malformed input), so
  // the announcement path can never break the rail render.
  const prevAlertsRef = React.useRef(alerts);
  const [announcement, setAnnouncement] = React.useState(null);
  React.useEffect(() => {
    const prev = prevAlertsRef.current;
    if (prev === alerts) return;
    prevAlertsRef.current = alerts;
    const next = buildAlertAnnouncement(prev, alerts);
    if (next) setAnnouncement(next);
  }, [alerts]);

  return (
    <section
      data-testid="alerts-rail"
      aria-label="Active alerts"
      style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        fontFamily: theme.fontUI,
        color: theme.textPrimary,
      }}
    >
      {/* GMA-1608 live regions: always mounted (a region added at announce
          time is not reliably spoken) and visually hidden via clip-rect
          (display:none would silence them). Exactly one region carries text
          per announcement, so priorities never clobber each other. */}
      <div
        data-testid="alerts-live-assertive"
        role="alert"
        aria-live="assertive"
        style={VISUALLY_HIDDEN_STYLE}
      >
        {announcement && announcement.level === "assertive"
          ? announcement.text
          : ""}
      </div>
      <div
        data-testid="alerts-live-polite"
        role="status"
        aria-live="polite"
        style={VISUALLY_HIDDEN_STYLE}
      >
        {announcement && announcement.level === "polite"
          ? announcement.text
          : ""}
      </div>
      {sorted.length === 0 ? (
        <p
          data-testid="alerts-rail-empty"
          style={{ padding: 16, margin: 0, color: theme.textMuted }}
        >
          No active alerts
        </p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {sorted.map((alert, index) => {
            const key =
              alert.id ?? `${alert.severity}-${alert.createdAt}-${index}`;
            const label = alert.type || "Alert";
            // One fallback used for BOTH the visible name and the aria-labels,
            // so assistive tech hears exactly what is rendered.
            const clientName = alert.clientName || "Unknown client";
            const open = () => selectable && onSelectAlert(alert);
            return (
              <li
                key={key}
                data-testid="alert-row"
                data-severity={alert.severity || "unknown"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 16px",
                  borderBottom: `1px solid ${theme.border}`,
                }}
              >
                {/* Keep the <li> as a listitem for list semantics; the
                    interactive role lives on this inner region, and Acknowledge
                    is a sibling (not nested), so neither swallows the other. */}
                <div
                  data-testid="alert-open"
                  role={selectable ? "button" : undefined}
                  tabIndex={selectable ? 0 : undefined}
                  aria-label={
                    selectable
                      ? `Open alert: ${label} for ${clientName}`
                      : undefined
                  }
                  onClick={selectable ? open : undefined}
                  onKeyDown={
                    selectable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            open();
                          }
                        }
                      : undefined
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flex: 1,
                    minWidth: 0,
                    cursor: selectable ? "pointer" : "default",
                  }}
                >
                  <SeverityBadge level={alert.severity} label={label} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block" }}>{clientName}</span>
                    {alert.location ? (
                      <span style={{ fontSize: 12, color: theme.textMuted }}>
                        {alert.location}
                      </span>
                    ) : null}
                  </span>
                  <span
                    style={{
                      fontFamily: theme.fontMono,
                      fontSize: 12,
                      color: theme.textMuted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatRelativeTime(alert.createdAt, now)}
                  </span>
                </div>
                {ackable ? (
                  <button
                    type="button"
                    data-testid="alert-acknowledge"
                    aria-label={`Acknowledge alert: ${label} for ${clientName}`}
                    onClick={() => onAcknowledge(alert)}
                    style={{
                      minHeight: 32,
                      padding: "4px 12px",
                      border: `1px solid ${theme.border}`,
                      borderRadius: 6,
                      background: theme.surface,
                      color: theme.textPrimary,
                      cursor: "pointer",
                      fontFamily: theme.fontUI,
                      fontSize: 12,
                    }}
                  >
                    Acknowledge
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

AlertsRail.propTypes = {
  /**
   * Active alerts; { id, severity, type, clientName, location, createdAt }.
   * Typed as a plain array (not arrayOf(object)) because the component is
   * intentionally fail-safe for null / null-or-malformed rows, and a strict
   * element type would emit console warnings for those supported inputs.
   */
  alerts: PropTypes.array,
  /** Reference time for elapsed display; defaults inside the model to Date.now(). */
  now: PropTypes.number,
  /** Called with the alert when a row is opened (parent opens the DetailDrawer). */
  onSelectAlert: PropTypes.func,
  /** Called with the alert when its Acknowledge button is pressed. */
  onAcknowledge: PropTypes.func,
};
